import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import SearchField from "../../atoms/inputs/SearchField"
import Dropdown, { optionsMap } from "../../atoms/inputs/Dropdown"
import { useTranslation } from "react-i18next"
import ListIcon from "../../atoms/icons/ListIcon"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import DetailsIcon from "../../atoms/icons/DetailsIcon"
import { theme } from "../../theme"
import IconButton from "../../atoms/buttons/IconButton"
import { formatApiOrgTerminals } from "../../../utils/formatters"
import OrganizationTerminalsCard from "../../organisms/OrganizationTerminalsCard"
import FilterDropdown from "../../atoms/inputs/FilterDropdown"
import { useOrganizations } from "../../../hooks/useOrganizations"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { TerminalSortings } from "../../../utils/filter-helpers"
import { useNavigate, useParams } from "react-router-dom"
import PaginationComponent from "../../molecules/PaginationComponent"
import { useAllTerminals } from "../../../hooks/useAllTerminals"
import { PAGINATION_VALUE, PAGINATION_VALUE_PRESETS } from "../../../utils/design-helpers"
import { Key } from "react-aria-components"
import Button from "../../atoms/buttons/Button"

const DROPDOWN_OPTIONS = [
  { id: "terminalName", value: "terminalName", name: "Name A-Z" },
  { id: "-terminalName", value: "-terminalName", name: "Name Z-A" },
  { id: TerminalSortings.dataPlanAsc, value: TerminalSortings.dataPlanAsc, name: "Data plan A-Z" },
  { id: TerminalSortings.dataPlanDesc, value: TerminalSortings.dataPlanDesc, name: "Data plan Z-A" },
  {
    value: "subOrganization",
    id: "subOrganization",
    name: "Suborganization A-Z",
  },
  {
    value: "-subOrganization",
    id: "-subOrganization",
    name: "Suborganization Z-A",
  },
  {
    value: TerminalSortings.serialNumberAsc,
    id: TerminalSortings.serialNumberAsc,
    name: "Serial number low - high",
  },
  {
    value: TerminalSortings.serialNumberDesc,
    id: TerminalSortings.serialNumberDesc,
    name: "Serial number high - low",
  },
]
const TerminalsPage: React.FunctionComponent = () => {
  const { t } = useTranslation()
  const { organizations } = useOrganizations()
  const { page } = useParams()
  const navigate = useNavigate()

  const [itemsPerPage, setItemsPerPage] = React.useState(PAGINATION_VALUE)

  const [currentPage, setCurrentPage] = React.useState<number>(parseInt(page ?? "1"))
  const [offset, setOffset] = React.useState<number>(currentPage > 1 ? (currentPage - 1) * itemsPerPage : 0)

  const [searchValue, setSearchValue] = React.useState<undefined | string>(undefined)
  const [sortingId, setSortingId] = React.useState<Key>("terminalName")

  const sorting = optionsMap(DROPDOWN_OPTIONS)[sortingId].value!

  const organizationsToUse: Array<{ id: number; title: string }> = []
  organizations?.map((org: any) => {
    organizationsToUse.push({ id: parseInt(org.id), title: org.name })
  })
  const organizationIds = organizationsToUse.map((org) => org.id)
  const [selectedFilterIds, setSelectedFilterIds] = React.useState<Array<number> | undefined>(undefined)

  const onClickFilterItem = (id: number) => {
    const idAsNumber = parseInt(id.toString())

    if (selectedFilterIds?.includes(idAsNumber)) {
      const filterd = selectedFilterIds.filter((x) => x !== idAsNumber)
      setSelectedFilterIds(filterd)
    } else {
      selectedFilterIds ? setSelectedFilterIds([...selectedFilterIds, idAsNumber]) : setSelectedFilterIds([idAsNumber])
    }
    onClickPage(1)
  }
  const onClickSelectAllFilterOptions = () => {
    setSelectedFilterIds(organizationIds)
    onClickPage(1)
  }

  const { allTerminals, isLoading } = useAllTerminals(itemsPerPage, offset, searchValue, selectedFilterIds, sorting)

  const formattedAllOrgTerminals = formatApiOrgTerminals(allTerminals?.edges ?? [])

  //Handle open sections
  const [cardsOpen, setCardsOpen] = React.useState<Array<string>>([])
  const checkCardIsOpen = (id: string) => {
    return cardsOpen.includes(id)
  }
  const onClickHandleOpenCard = (id: string) => {
    if (checkCardIsOpen(id)) {
      const arr = cardsOpen.filter((x) => x !== id)
      setCardsOpen(arr)
    } else {
      setCardsOpen([...cardsOpen, id])
    }
  }
  const onClickListView = () => {
    setCardsOpen([])
  }
  const allTerminalIds: Array<any> = []
  formattedAllOrgTerminals.map((terminal: any) => allTerminalIds.push(terminal.id))
  const onClickDetailsView = () => {
    setCardsOpen(allTerminalIds)
  }

  const onSearch = (value: any) => {
    setSearchValue(value)
    onClickPage(1)
  }

  const onSetSortingId = (id: Key) => {
    setSortingId(id)
    onClickPage(1)
  }

  //Handle pagination
  const totalCount = allTerminals?.totalCount ?? 1
  const pagesInitialValue = Math.ceil(totalCount / itemsPerPage)
  const totalPages = totalCount < itemsPerPage ? 1 : pagesInitialValue
  const onClickPage = (newPage: number) => {
    if (newPage !== currentPage) {
      setOffset(itemsPerPage * (newPage - 1))
      setCurrentPage(newPage)
      navigate(`/organization/terminals/${newPage}`)
    }
  }

  return (
    <div>
      <Row align="end">
        <Col lg={2} md={4} xs={12}>
          <SearchField onChange={(e) => onSearch(e)} />
          <Visible xs>
            <HorizontalSpacer spacing={2} />
          </Visible>
        </Col>
        <Col lg={2} md={4} xs={7}>
          <Dropdown
            options={DROPDOWN_OPTIONS}
            label={t("sort_by")}
            onSelectionChange={onSetSortingId}
            selectedKey={sortingId}
          />
        </Col>
        <Hidden xs md>
          <Col md={2}>
            <FilterDropdown
              items={organizationsToUse}
              title="Filter"
              onClickItem={(e) => onClickFilterItem(e)}
              selectedValues={selectedFilterIds}
              selectAllTitle={t("all_organizations")}
              onClickSelectAll={onClickSelectAllFilterOptions}
            />
          </Col>
        </Hidden>
        <Col lg={2} md={3} xs={5}>
          <div style={{ display: "flex" }}>
            <Button
              iconLeft={<ListIcon small color={cardsOpen.length < 1 ? theme.colors.white : theme.colors.greyDark} />}
              onPress={onClickListView}
              variant={cardsOpen.length < 1 ? "dark" : "light"}
            />
            <Spacer spacing={1} />
            <Button
              variant={cardsOpen.length === allTerminalIds.length ? "dark" : "light"}
              iconLeft={
                <DetailsIcon
                  small
                  color={cardsOpen.length === allTerminalIds.length ? theme.colors.white : theme.colors.greyDark}
                />
              }
              onPress={onClickDetailsView}
            />
          </div>
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <OrganizationTerminalsCard
          terminals={formattedAllOrgTerminals ?? []}
          cardsOpen={cardsOpen}
          onClickHandleOpenCard={onClickHandleOpenCard}
        />
      )}

      <HorizontalSpacer spacing={2} />
      <Row justify="center">
        <PaginationComponent
          pages={totalPages}
          currentPageIndex={currentPage}
          onClickPage={onClickPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(n) => setItemsPerPage(n)}
          itemsPerPageValues={PAGINATION_VALUE_PRESETS}
        />
      </Row>
      <HorizontalSpacer spacing={2} />
    </div>
  )
}

export default TerminalsPage
